import Switch from '@eduzz/houston-ui/Forms/Switch';
import Table from '@eduzz/houston-ui/Table';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from 'components/Alert';
import React, { memo, useCallback, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { useWebHook, useWebHookScreen } from 'v2/components/Pages/Webhook/context';
import ModalTest from 'v2/components/Pages/Webhook/ModalTest';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import webhookService from 'v2/services/webhook';

import type { IWebHookList } from 'v2/interfaces/webhook/webhook';

const Types = {
  ['invoice']: 'Fatura',
  ['abandonment']: 'Abandono de carrinho',
};

import ModalEdit from '../ModalEdit';
import styles from './styles';

const List = memo(() => {
  const { loading } = useWebHookScreen();
  const { list, setList } = useWebHook();
  const classes = styles();

  const [webhookActive, setWebhookActive] = useState<IWebHookList | null>(null);
  const [modalTest, setModalTest] = useState<boolean>(false);
  const [modalEdit, setModalEdit] = useState<boolean>(false);
  const [copyWebhook, setCopyWebhook] = useState<string>('');

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:webhook:editar');

  const [remove] = useCallbackObservable(
    (id) =>
      webhookService.removeWebhook(id).pipe(
        tap(
          () => {
            toast.success('Webhook removido com sucesso!');
          },
          (err: boolean | React.ReactFragment | React.ReactChild | React.ReactPortal | (() => void)) => {
            toast.error(err);
          }
        )
      ),
    []
  );

  const [changeUrl] = useCallbackObservable(
    (id, status) =>
      webhookService.changeUrlStatus(id, status).pipe(
        tap(
          () => {
            toast.success('Status atualizado com sucesso');
          },
          (err: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | (() => void)) => {
            toast.error(err);
          }
        )
      ),
    []
  );

  const onChangeStatus = useCallback(
    (id: string, status: boolean) => {
      if (!editPermission) {
        toast.error('Sem permissão para alterar status do webhook');
        return;
      }
      const newList = list;
      const index = newList.findIndex((item) => item._id === id);
      newList[index].status = status;

      setList([...newList]);
      changeUrl(id, status);
    },
    [editPermission, changeUrl, list, setList]
  );

  const handleClickRemove = useCallback(
    async (webhook) => {
      setWebhookActive(webhook);
      const confirm = await Alert.show({
        message: 'Deseja realmente excluir a url?',
        title: 'Atenção!',
        confirmation: true,
      });

      if (!confirm) {
        return;
      }

      const newList = list.filter((item) => item._id !== webhook?._id);
      setList([...newList]);
      remove(webhook._id);
    },
    [list, setList, remove]
  );

  const onClickEdit = useCallback(
    (webhook) => {
      setWebhookActive(webhook);
      setModalEdit(true);
    },
    [setModalEdit]
  );

  const onClickTest = useCallback((webhook) => {
    setWebhookActive(webhook);
    setModalTest(true);
  }, []);

  const handleClickCopy = useCallback(
    (webhook) => {
      setCopyWebhook(webhook._id);

      setTimeout(() => {
        setCopyWebhook('');
      }, 1500);
    },
    [setCopyWebhook]
  );
  return (
    <>
      <Table total={list.length} loading={loading}>
        <Table.Header>
          <Table.Column>Ativo</Table.Column>
          <Table.Column>Nome</Table.Column>
          <Table.Column>Tipo</Table.Column>
          <Table.Column>Produto</Table.Column>
          <Table.Column>URL</Table.Column>
        </Table.Header>
        <Table.Body>
          {list.map((item, index) => (
            <Table.Row data={item} index={index} key={item._id}>
              <Table.Cell>
                <Switch name='status' value={item.status} onChange={(status) => onChangeStatus(item._id, status)} />
              </Table.Cell>
              <Table.Cell>{item.description}</Table.Cell>
              <Table.Cell>{Types[item.type]}</Table.Cell>
              <Table.Cell>
                {item.product_id} - {item.product_name}
              </Table.Cell>
              <Table.Cell>
                <div className={classes.copy}>
                  <Truncate count={40} final='...' title={item.url ? item.url : '-'}>
                    {item.url ? item.url : '-'}
                  </Truncate>

                  {item.url && (
                    <Tooltip title={copyWebhook === item._id ? 'Copiado!' : 'Clique para copiar'} placement='top'>
                      <CopyToClipboard text={item.url} onCopy={() => handleClickCopy(item._id)}>
                        <Icon>content_copy</Icon>
                      </CopyToClipboard>
                    </Tooltip>
                  )}
                </div>
              </Table.Cell>
              <PermissionHide action='myeduzz:webhook:editar'>
                <Table.Action onClick={onClickEdit}>Editar</Table.Action>
              </PermissionHide>
              <Table.Action onClick={onClickTest}>Enviar Teste</Table.Action>
              <PermissionHide action='myeduzz:webhook:editar'>
                <Table.Action onClick={handleClickRemove}>Remover</Table.Action>
              </PermissionHide>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <ModalTest open={modalTest} webhook={webhookActive} close={setModalTest} />
      <ModalEdit open={modalEdit} close={() => setModalEdit(false)} webhook={webhookActive} />
    </>
  );
});

export default List;
